var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import './BasicMap';
// ── Role palette (flat, no gradient) ────────────────────────────
const ROLE_COLOR = {
    own: '#08c1ff', // network blue (same as <poi-map>)
    competitor: '#EF4444', // modern red
    attractor: '#10B981', // modern green
};
// Ghost pin colour (add change with no poi_id yet — brand-new POI).
const NEW_PIN_COLOR = '#F97316'; // orange
const NEW_PIN_IMAGE_ID = 'scenario-map-pin-new';
// ── Operation type palette (used by the top-right badge symbol) ────
const OPERATION_COLOR = {
    add: '#10B981',
    edit: '#3B82F6',
    delete: '#EF4444',
};
const OPERATION_GLYPH = {
    add: '+',
    edit: '✎',
    delete: '×',
};
const ROLE_LABEL = () => ({
    own: msg('Own', { id: 'scenarioMap.role.own', desc: 'Network role label: own network' }),
    competitor: msg('Competitors', { id: 'scenarioMap.role.competitor', desc: 'Network role label: competitors' }),
    attractor: msg('Attractors', { id: 'scenarioMap.role.attractor', desc: 'Network role label: attractors' }),
});
const SOURCE_ID = 'scenario-map-source';
const PIN_LAYER_ID = 'scenario-map-pins';
const BADGE_LAYER_ID = 'scenario-map-badges';
const PIN_IMAGE_PREFIX_NET = 'scenario-map-pin-net-'; // + network_id
const PIN_IMAGE_PREFIX_ROLE = 'scenario-map-pin-role-'; // + role (ghost fallback)
const BADGE_IMAGE_PREFIX = 'scenario-map-badge-'; // + change type
function escapeXmlAttr(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
/**
 * Same pin geometry as `<poi-map>`. When `logoDataUrl` is provided, the
 * logo is embedded in the pin head inside a circular clip path.
 */
function buildPinSvg(fill, logoDataUrl) {
    if (logoDataUrl) {
        return `
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 22.5 30">
        <path fill="${fill}" d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z"/>
        <circle fill="#fff" cx="11.25" cy="11.25" r="8"/>
        <clipPath id="logoClip"><circle cx="11.25" cy="11.25" r="7"/></clipPath>
        <image href="${escapeXmlAttr(logoDataUrl)}" x="4.25" y="4.25" width="14" height="14" clip-path="url(#logoClip)" preserveAspectRatio="xMidYMid slice"/>
      </svg>
    `;
    }
    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 22.5 30">
      <path d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z" fill="${fill}"/>
      <circle cx="11.25" cy="11.25" r="4" fill="#ffffff"/>
    </svg>
  `;
}
/**
 * Fetch an image URL and return it as a base64 data URL. Browsers refuse
 * to load external images referenced inside an SVG that itself is served
 * from a data: URL, so we inline the logo as base64. Copied from
 * `<study-creator-map>`.
 */
async function fetchAsDataUrl(url) {
    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok)
            return null;
        const blob = await response.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    }
    catch {
        return null;
    }
}
/**
 * 16x16 circular badge with a 1-char glyph, white border. Rasterised to
 * 64x64 for crispness at the layer's icon-size.
 */
function buildBadgeSvg(fill, glyph) {
    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 16 16">
      <circle cx="8" cy="8" r="7" fill="${fill}" stroke="#ffffff" stroke-width="1.5"/>
      <text x="8" y="11.2" font-size="9.5" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle" fill="#ffffff">${glyph}</text>
    </svg>
  `;
}
/**
 * ScenarioMap — renders universe network POIs as `<poi-map>`-style pins
 * (same SVG path, per-role colour) via a Mapbox symbol layer (pins stay
 * anchored to their coordinates, no DOM-marker drift). Changed POIs get
 * a top-right badge rendered by a secondary symbol layer offset to the
 * pin head's top-right corner.
 *
 * A layers panel (top-right) toggles each network's visibility by
 * filtering the main layer on `network_id`.
 */
let ScenarioMap = class ScenarioMap extends LitElement {
    constructor() {
        super(...arguments);
        this.networks = [];
        this.operations = [];
        this.mapOptions = {};
        this.autoFit = true;
        this.selectedOperationId = null;
        this.defaultHiddenRoles = [
            'competitor',
            'attractor',
        ];
        /**
         * When true, a small "Loading networks…" pill is shown at the top-center
         * of the map. Toggled by the caller while background POI / logo fetches
         * are in flight.
         */
        this.loading = false;
        this.loadingMessage = null;
        this.mapInstance = null;
        this.hiddenNetworkIds = new Set();
        this.layersPanelOpen = true;
        this.mapLib = null;
        this.imagesLoaded = false;
        this.hasFitOnce = false;
        this.handlersBound = false;
        this.hasAppliedDefaultHidden = false;
        this.handleMapReady = async (event) => {
            this.mapInstance = event.detail.map;
            this.mapLib = event.detail.mapLib;
            // Kill drag inertia via the public pan-handler API.
            try {
                this.mapInstance?.dragPan?.enable?.({
                    linearity: 0.3,
                    easing: (t) => t,
                    maxSpeed: 0,
                    deceleration: 2500,
                });
            }
            catch {
                /* noop */
            }
            await this.loadImages();
            this.imagesLoaded = true;
            this.drawPois();
            if (this.autoFit && !this.hasFitOnce)
                this.flyToAll();
            this.bindClickHandlers();
            this.dispatchEvent(new CustomEvent('map-loaded', {
                detail: { map: this.mapInstance, mapLib: this.mapLib },
            }));
        };
    }
    updated(changed) {
        if (changed.has('networks') && !this.hasAppliedDefaultHidden) {
            this.applyDefaultHiddenRoles();
        }
        if (this.mapInstance &&
            this.imagesLoaded &&
            (changed.has('networks') ||
                changed.has('operations') ||
                changed.has('hiddenNetworkIds') ||
                changed.has('selectedOperationId'))) {
            // Re-register per-network pin images when the networks list changes
            // (colour / logo can vary between updates) before redrawing.
            const refresh = changed.has('networks')
                ? this.loadImages().then(() => this.drawPois())
                : Promise.resolve(this.drawPois());
            void refresh.then(() => {
                if (this.autoFit &&
                    !this.hasFitOnce &&
                    (changed.has('networks') || changed.has('operations'))) {
                    this.flyToAll();
                }
            });
        }
    }
    /**
     * First time `networks` arrives non-empty, hide every network whose role
     * is in `defaultHiddenRoles`. Only runs once so the user's subsequent
     * manual toggles are preserved.
     */
    applyDefaultHiddenRoles() {
        if (this.networks.length === 0)
            return;
        this.hasAppliedDefaultHidden = true;
        const hidden = new Set(this.hiddenNetworkIds);
        for (const n of this.networks) {
            if (this.defaultHiddenRoles.includes(n.role)) {
                hidden.add(n.network_id);
            }
        }
        this.hiddenNetworkIds = hidden;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupMap();
    }
    getMapInstance() {
        return this.mapInstance;
    }
    getMapLib() {
        return this.mapLib;
    }
    flyToAll() {
        if (!this.mapInstance || !this.mapLib)
            return;
        const bounds = new this.mapLib.LngLatBounds();
        for (const n of this.networks) {
            if (this.hiddenNetworkIds.has(n.network_id))
                continue;
            for (const p of n.pois)
                bounds.extend([p.longitude, p.latitude]);
        }
        for (const c of this.ghostOperations()) {
            bounds.extend([c.longitude, c.latitude]);
        }
        if (!bounds.isEmpty()) {
            this.mapInstance.fitBounds(bounds, { padding: 60 });
            this.hasFitOnce = true;
        }
    }
    ghostOperations() {
        return this.operations.filter((c) => c.type === 'add' && c.poi_id == null);
    }
    loadImageFromSvg(imageId, svg) {
        return new Promise((resolve) => {
            const img = new Image();
            img.src =
                'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
            img.onload = () => {
                if (this.mapInstance.hasImage(imageId)) {
                    this.mapInstance.removeImage(imageId);
                }
                this.mapInstance.addImage(imageId, img, { sdf: false });
                resolve();
            };
            img.onerror = () => resolve();
        });
    }
    async loadImages() {
        if (!this.mapInstance)
            return;
        const jobs = [];
        // Per-network pin image — colour + optional logo inlined in the SVG.
        for (const net of this.networks) {
            jobs.push((async () => {
                const fill = net.color ?? ROLE_COLOR[net.role];
                const logoDataUrl = net.logoUrl
                    ? await fetchAsDataUrl(net.logoUrl)
                    : null;
                await this.loadImageFromSvg(PIN_IMAGE_PREFIX_NET + net.network_id, buildPinSvg(fill, logoDataUrl));
            })());
        }
        // Role-based fallback (rarely needed — used only when a POI references
        // a network that wasn't provided in `networks`).
        for (const [role, fill] of Object.entries(ROLE_COLOR)) {
            jobs.push(this.loadImageFromSvg(PIN_IMAGE_PREFIX_ROLE + role, buildPinSvg(fill, null)));
        }
        // Ghost "new POI" pin — orange, no logo.
        jobs.push(this.loadImageFromSvg(NEW_PIN_IMAGE_ID, buildPinSvg(NEW_PIN_COLOR, null)));
        // Operation-type badges (top-right notification on POIs with operations).
        for (const [type, fill] of Object.entries(OPERATION_COLOR)) {
            jobs.push(this.loadImageFromSvg(BADGE_IMAGE_PREFIX + type, buildBadgeSvg(fill, OPERATION_GLYPH[type])));
        }
        await Promise.all(jobs);
    }
    /** Build the combined FeatureCollection (network POIs + ghost add operations). */
    buildFeatureCollection() {
        const operationByPoi = new Map();
        for (const c of this.operations) {
            if (c.poi_id != null)
                operationByPoi.set(c.poi_id, c);
        }
        const features = [];
        for (const net of this.networks) {
            if (this.hiddenNetworkIds.has(net.network_id))
                continue;
            for (const poi of net.pois) {
                const operation = operationByPoi.get(poi.poi_id) ?? null;
                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [poi.longitude, poi.latitude],
                    },
                    properties: {
                        role: net.role,
                        network_id: net.network_id,
                        poi_id: poi.poi_id,
                        name: poi.name ?? null,
                        operation_type: operation?.type ?? null,
                        operation_id: operation?.operation_id ?? null,
                        selected: operation != null &&
                            operation.operation_id === this.selectedOperationId,
                    },
                });
            }
        }
        for (const c of this.ghostOperations()) {
            const net = c.network_id
                ? this.networks.find((n) => n.network_id === c.network_id)
                : null;
            if (net && this.hiddenNetworkIds.has(net.network_id))
                continue;
            const role = net?.role ?? 'own';
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [c.longitude, c.latitude],
                },
                properties: {
                    role,
                    network_id: net?.network_id ?? null,
                    poi_id: null,
                    name: c.label ?? null,
                    operation_type: 'add',
                    operation_id: c.operation_id,
                    selected: c.operation_id === this.selectedOperationId,
                },
            });
        }
        return { type: 'FeatureCollection', features };
    }
    drawPois() {
        if (!this.mapInstance || !this.mapLib)
            return;
        const fc = this.buildFeatureCollection();
        // (Re)create the source so data stays fresh.
        if (this.mapInstance.getLayer(BADGE_LAYER_ID)) {
            this.mapInstance.removeLayer(BADGE_LAYER_ID);
        }
        if (this.mapInstance.getLayer(PIN_LAYER_ID)) {
            this.mapInstance.removeLayer(PIN_LAYER_ID);
        }
        if (this.mapInstance.getSource(SOURCE_ID)) {
            this.mapInstance.removeSource(SOURCE_ID);
        }
        this.mapInstance.addSource(SOURCE_ID, {
            type: 'geojson',
            data: fc,
        });
        // ── Pin symbol layer ────────────────────────────────────
        // If network_id is known → per-network image (with logo + colour),
        // otherwise use the orange "new" ghost pin.
        //
        // Input is wrapped in `coalesce` so a feature with `network_id: null`
        // (ghost-add changes without a parent network) doesn't trip
        // MapLibre's "Expected number, got null" type warning during the
        // worker tile parse. The outer `case` below still routes nulls to
        // `NEW_PIN_IMAGE_ID` first — `coalesce` is just belt-and-braces for
        // the parser's eager type inference on the `match` branch.
        const networkMatchExpr = [
            'match',
            ['coalesce', ['get', 'network_id'], -1],
        ];
        for (const net of this.networks) {
            networkMatchExpr.push(net.network_id, PIN_IMAGE_PREFIX_NET + net.network_id);
        }
        // `match` default must be a concrete expression — reuse the role fallback.
        networkMatchExpr.push(PIN_IMAGE_PREFIX_ROLE + 'own');
        // No network_id → this is a ghost "add" change (new POI). Use the
        // dedicated orange pin. Otherwise route via the per-network image,
        // with the role fallback for unknown network_ids.
        const pinIconExpr = [
            'case',
            ['==', ['get', 'network_id'], null],
            NEW_PIN_IMAGE_ID,
            networkMatchExpr,
        ];
        this.mapInstance.addLayer({
            id: PIN_LAYER_ID,
            type: 'symbol',
            source: SOURCE_ID,
            layout: {
                'icon-image': pinIconExpr,
                // Use rem-size 0.5 (100x100 svg → 50x50 on map), slightly larger
                // when selected.
                'icon-size': [
                    'case',
                    ['boolean', ['get', 'selected'], false],
                    0.42,
                    0.35,
                ],
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
            },
        });
        // ── Badge symbol layer (only features with operation_type) ─
        const badgeIconExpr = ['match', ['get', 'operation_type']];
        for (const t of Object.keys(OPERATION_COLOR)) {
            badgeIconExpr.push(t, BADGE_IMAGE_PREFIX + t);
        }
        badgeIconExpr.push(BADGE_IMAGE_PREFIX + 'add'); // fallback
        this.mapInstance.addLayer({
            id: BADGE_LAYER_ID,
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['all', ['has', 'operation_type'], ['!=', ['get', 'operation_type'], null]],
            layout: {
                'icon-image': badgeIconExpr,
                'icon-size': 0.25,
                'icon-anchor': 'center',
                // Position the badge at the pin head's top-right. icon-offset is
                // in image-pixels scaled by icon-size — these values shift the
                // badge by ~11 px right and ~22 px up on screen at size 0.25.
                'icon-offset': [45, -90],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
            },
        });
    }
    bindClickHandlers() {
        if (!this.mapInstance || this.handlersBound)
            return;
        this.handlersBound = true;
        const onClick = (e) => {
            const feature = e.features?.[0];
            if (!feature)
                return;
            const operationId = feature.properties?.operation_id;
            if (operationId == null)
                return;
            const operation = this.operations.find((c) => c.operation_id === operationId);
            if (!operation)
                return;
            this.dispatchEvent(new CustomEvent('operation-selected', {
                detail: { operation },
                bubbles: true,
                composed: true,
            }));
        };
        this.mapInstance.on('click', PIN_LAYER_ID, onClick);
        this.mapInstance.on('mouseenter', PIN_LAYER_ID, (e) => {
            const feature = e.features?.[0];
            if (feature?.properties?.operation_id != null) {
                this.mapInstance.getCanvas().style.cursor = 'pointer';
            }
        });
        this.mapInstance.on('mouseleave', PIN_LAYER_ID, () => {
            this.mapInstance.getCanvas().style.cursor = '';
        });
    }
    cleanupMap() {
        if (!this.mapInstance)
            return;
        try {
            if (this.mapInstance.getLayer(BADGE_LAYER_ID)) {
                this.mapInstance.removeLayer(BADGE_LAYER_ID);
            }
            if (this.mapInstance.getLayer(PIN_LAYER_ID)) {
                this.mapInstance.removeLayer(PIN_LAYER_ID);
            }
            if (this.mapInstance.getSource(SOURCE_ID)) {
                this.mapInstance.removeSource(SOURCE_ID);
            }
        }
        catch {
            /* noop */
        }
    }
    toggleNetwork(networkId) {
        const next = new Set(this.hiddenNetworkIds);
        if (next.has(networkId))
            next.delete(networkId);
        else
            next.add(networkId);
        this.hiddenNetworkIds = next;
    }
    renderLayersToggle() {
        return html `
      <div class="layers-toggle">
        <button
          class="layers-button"
          title=${msg('Layers', { id: 'scenarioMap.layers', desc: 'Layers panel toggle button title' })}
          @click=${() => (this.layersPanelOpen = !this.layersPanelOpen)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
            <polyline points="2 17 12 22 22 17"></polyline>
            <polyline points="2 12 12 17 22 12"></polyline>
          </svg>
        </button>
        ${this.layersPanelOpen
            ? html `
              <div class="layers-panel">
                <h4>${msg('Networks', { id: 'scenarioMap.networks', desc: 'Networks section heading in layers panel' })}</h4>
                ${this.networks.length === 0
                ? html `<div style="color:#94a3b8;">${msg('No networks', { id: 'scenarioMap.noNetworks', desc: 'Empty state when there are no networks' })}</div>`
                : this.networks.map((n) => {
                    // Use the same colour we paint the pin with so the
                    // dot in the panel matches what the analyst sees.
                    const color = n.color ?? ROLE_COLOR[n.role];
                    const visible = !this.hiddenNetworkIds.has(n.network_id);
                    return html `
                        <label
                          class="layer-row"
                          @click=${(e) => e.stopPropagation()}
                        >
                          <span class="dot" style="background:${color};"></span>
                          <span
                            class="name"
                            title="${n.network_name} (${ROLE_LABEL()[n.role]})"
                            >${n.network_name}</span
                          >
                          <span class="count">${n.pois.length}</span>
                          <input
                            type="checkbox"
                            .checked=${visible}
                            @change=${() => this.toggleNetwork(n.network_id)}
                          />
                        </label>
                      `;
                })}
              </div>
            `
            : nothing}
      </div>
    `;
    }
    renderLegend() {
        const entries = [
            { type: 'add', label: msg('Add', { id: 'scenarioMap.legend.add', desc: 'Legend label for add operation' }) },
            { type: 'edit', label: msg('Edit', { id: 'scenarioMap.legend.edit', desc: 'Legend label for edit operation' }) },
            { type: 'delete', label: msg('Delete', { id: 'scenarioMap.legend.delete', desc: 'Legend label for delete operation' }) },
        ];
        return html `
      <div class="legend">
        ${entries.map((e) => html `
            <span class="entry">
              <span
                class="dot"
                style="background:${OPERATION_COLOR[e.type]};"
              ></span>
              ${e.label}
            </span>
          `)}
      </div>
    `;
    }
    renderLoading() {
        if (!this.loading)
            return nothing;
        return html `
      <div class="loading-pill" role="status" aria-live="polite">
        <span class="spinner"></span>
        <span>${this.loadingMessage ?? msg('Loading networks…', { id: 'map.loadingNetworks', desc: 'Loading indicator while networks are being fetched' })}</span>
      </div>
    `;
    }
    render() {
        return html `
      <basic-map
        .mapOptions=${this.mapOptions}
        @map-loaded=${this.handleMapReady}
      >
        <slot></slot>
      </basic-map>
      ${this.renderLoading()}${this.renderLayersToggle()}${this.renderLegend()}
    `;
    }
};
ScenarioMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }
    basic-map {
      width: 100%;
      height: 100%;
      display: block;
    }

    .layers-toggle {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 6;
      font-family: inherit;
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      gap: 6px;
    }
    .layers-button {
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px;
      font-size: 12px;
      cursor: pointer;
      color: #333;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 32px;
      height: 32px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
      flex-shrink: 0;
    }
    .layers-button:hover {
      border-color: #999;
    }
    .layers-panel {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px 10px;
      min-width: 220px;
      font-size: 12px;
      color: #333;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    .layers-panel h4 {
      margin: 0 0 6px;
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
    .layer-row {
      display: grid;
      grid-template-columns: 14px 1fr auto auto;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      cursor: pointer;
      user-select: none;
    }
    .layer-row:hover {
      background: #f8fafc;
    }
    .layer-row .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }
    .layer-row .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .layer-row .count {
      color: #94a3b8;
      font-variant-numeric: tabular-nums;
    }
    .layer-row input[type='checkbox'] {
      margin: 0;
    }
    .loading-pill {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 7;
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      padding: 6px 14px;
      font-family: inherit;
      font-size: 12px;
      color: #333;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .loading-pill .spinner {
      width: 12px;
      height: 12px;
      border: 2px solid #ddd;
      border-top-color: #0078d4;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .legend {
      position: absolute;
      bottom: 12px;
      left: 12px;
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px 12px;
      font-family: inherit;
      font-size: 12px;
      color: #333;
      display: flex;
      gap: 14px;
      z-index: 5;
    }
    .legend .entry {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .legend .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      display: inline-block;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }
  `;
__decorate([
    property({ type: Array })
], ScenarioMap.prototype, "networks", void 0);
__decorate([
    property({ type: Array })
], ScenarioMap.prototype, "operations", void 0);
__decorate([
    property({ type: Object })
], ScenarioMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: Boolean, attribute: 'auto-fit' })
], ScenarioMap.prototype, "autoFit", void 0);
__decorate([
    property({ type: Number, attribute: 'selected-operation-id' })
], ScenarioMap.prototype, "selectedOperationId", void 0);
__decorate([
    property({ type: Array })
], ScenarioMap.prototype, "defaultHiddenRoles", void 0);
__decorate([
    property({ type: Boolean })
], ScenarioMap.prototype, "loading", void 0);
__decorate([
    property({ type: String, attribute: 'loading-message' })
], ScenarioMap.prototype, "loadingMessage", void 0);
__decorate([
    state()
], ScenarioMap.prototype, "mapInstance", void 0);
__decorate([
    state()
], ScenarioMap.prototype, "hiddenNetworkIds", void 0);
__decorate([
    state()
], ScenarioMap.prototype, "layersPanelOpen", void 0);
ScenarioMap = __decorate([
    localized(),
    customElement('scenario-map')
], ScenarioMap);
export { ScenarioMap };
//# sourceMappingURL=ScenarioMap.js.map